import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { complianceApi, vehiclesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, FileCheck, Loader2 } from 'lucide-react';

const COMPLIANCE_TYPES = ['insurance', 'permit', 'fitness', 'puc', 'road_tax'];

const initialFormState = {
  vehicle_id: '',
  compliance_type: '',
  document_number: '',
  issue_date: '',
  expiry_date: '',
  amount: '',
  vendor_name: '',
  status: 'valid',
  notes: '',
};

const Compliance = () => {
  const [compliance, setCompliance] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [complianceRes, vehiclesRes] = await Promise.all([
        complianceApi.getAll(),
        vehiclesApi.getAll(),
      ]);
      setCompliance(complianceRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      await complianceApi.create(data);
      toast.success('Compliance record added');

      setDialogOpen(false);
      setFormData(initialFormState);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusBadge = (status, expiryDate) => {
    if (expiryDate) {
      const today = new Date();
      const expiry = new Date(expiryDate);
      const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      
      if (daysLeft < 0) {
        return <Badge className="bg-red-100 text-red-700 border-red-200">Expired</Badge>;
      } else if (daysLeft <= 7) {
        return <Badge className="bg-red-100 text-red-700 border-red-200">Critical</Badge>;
      } else if (daysLeft <= 30) {
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Expiring Soon</Badge>;
      }
    }
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Valid</Badge>;
  };

  const getVehicleName = (id) => {
    const v = vehicles.find(v => v.id === id);
    return v ? v.registration_number : 'N/A';
  };

  return (
    <Layout
      title="Compliance"
      subtitle="Document & Legal Management"
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setFormData(initialFormState);
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="add-compliance-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Add Compliance Document
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle *</Label>
                  <Select
                    value={formData.vehicle_id}
                    onValueChange={(v) => handleSelectChange('vehicle_id', v)}
                  >
                    <SelectTrigger data-testid="compliance-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.registration_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Document Type *</Label>
                  <Select
                    value={formData.compliance_type}
                    onValueChange={(v) => handleSelectChange('compliance_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPLIANCE_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type.replace('_', ' ').toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="document_number">Document Number *</Label>
                  <Input
                    id="document_number"
                    name="document_number"
                    placeholder="e.g., POL-123456"
                    value={formData.document_number}
                    onChange={handleInputChange}
                    required
                    data-testid="compliance-doc-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (₹) *</Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    min="0"
                    placeholder="e.g., 15000"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue_date">Issue Date *</Label>
                  <Input
                    id="issue_date"
                    name="issue_date"
                    type="date"
                    value={formData.issue_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date *</Label>
                  <Input
                    id="expiry_date"
                    name="expiry_date"
                    type="date"
                    value={formData.expiry_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="vendor_name">Vendor/Agent</Label>
                  <Input
                    id="vendor_name"
                    name="vendor_name"
                    placeholder="e.g., RTO Agent Name"
                    value={formData.vendor_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="compliance-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Add Document'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="compliance-page">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vehicle</TableHead>
                <TableHead>Document Type</TableHead>
                <TableHead>Document Number</TableHead>
                <TableHead>Issue Date</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : compliance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No compliance records found</p>
                  </TableCell>
                </TableRow>
              ) : (
                compliance.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{getVehicleName(item.vehicle_id)}</TableCell>
                    <TableCell className="capitalize">{item.compliance_type.replace('_', ' ')}</TableCell>
                    <TableCell>{item.document_number}</TableCell>
                    <TableCell>{item.issue_date}</TableCell>
                    <TableCell>{item.expiry_date}</TableCell>
                    <TableCell className="tabular-nums">{formatCurrency(item.amount)}</TableCell>
                    <TableCell>{getStatusBadge(item.status, item.expiry_date)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};

export default Compliance;
